import { ShoppingCart, Trash2, Minus, Plus } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { useCart } from '@/contexts/CartContext'
import { useNavigate } from 'react-router-dom'

const CartDrawer = () => {
  const { items, removeItem, updateQuantity, getTotalItems, getTotalPrice } =
    useCart()
  const navigate = useNavigate()

  const handleCheckout = () => {
    navigate('/checkout')
  }

  const formatOrientation = (orientation: string) => {
    if (orientation === 'quadrado-grande') {
      return 'Quadrado Grande'
    }
    if (orientation === 'paisagem-grande') {
      return 'Paisagem Grande'
    }
    return orientation.charAt(0).toUpperCase() + orientation.slice(1)
  }

  const formatCoverType = (coverType?: string) => {
    if (!coverType) return null
    const coverLabels: Record<string, string> = {
      fotografica: 'Capa Fotográfica',
      linho: 'Capa de Linho',
      couro: 'Capa de Couro',
      'couro-premium': 'Capa de Couro',
      acrilico: 'Capa de Acrílico',
    }
    return coverLabels[coverType] || coverType
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative border-primary text-primary hover:bg-primary/10"
          aria-label="Abrir carrinho de compras"
        >
          <ShoppingCart className="h-5 w-5" />
          {getTotalItems() > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
              {getTotalItems()}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Carrinho de Compras</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full pt-6">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <ShoppingCart className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-semibold mb-2">
                Seu carrinho está vazio
              </p>
              <p className="text-muted-foreground">
                Adicione produtos para começar suas compras
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto space-y-4">
                {items.map(item => (
                  <div
                    key={item.id}
                    className="flex gap-4 border rounded-lg p-4"
                  >
                    <img
                      src={item.productImage}
                      alt={item.productName}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1 space-y-2">
                      <h4 className="font-semibold">{item.productName}</h4>
                      <p className="text-sm text-muted-foreground">
                        {formatOrientation(item.orientation)} • {item.pages}{' '}
                        páginas
                        {item.coverType &&
                          ` • ${formatCoverType(item.coverType)}`}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(item.id, item.quantity - 1)
                            }
                            aria-label="Diminuir quantidade"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-semibold">
                            {item.quantity}
                          </span>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(item.id, item.quantity + 1)
                            }
                            aria-label="Aumentar quantidade"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => removeItem(item.id)}
                          aria-label="Remover item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="font-semibold text-primary">
                        R${' '}
                        {(item.price * item.quantity).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total:</span>
                  <span className="text-2xl text-primary">
                    R$ {getTotalPrice().toLocaleString('pt-BR')}
                  </span>
                </div>
                <Button
                  onClick={() => navigate('/carrinho')}
                  className="w-full"
                  size="lg"
                >
                  Ir para o Carrinho
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default CartDrawer
